/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './player-course-collections.stache';
import FeedService from 'scripts/services/base-feed-service';
import dtm from 'scripts/services/dtm';
import can from 'can';
import Swiper from 'swiper';
import urlUtil from 'scripts/utils/url';
import appContext from 'scripts/app-context';
import DomEventsManager from 'scripts/dom-events-manager';
import _ from 'lodash';

const playerCourseCollectionFeedURL =
    window.globalSettings.feeds.playerCourseCollectionFeed;
const playerCourseAvailableFiltersURL =
    window.globalSettings.feeds.playerCourseAvailableFilters;

const encodeItemIdentifier = (decoded) =>
    decoded.replace(/\s/g, '-').toLowerCase();
const mapFeedItemToEntry = (item) => {
    return {
        identifier: item.value,
        entry: item.text,
        hash: encodeItemIdentifier(item.text),
    };
};
const mapPlayersFeedToSelectorItems = (feed) => {
    return feed.map(mapFeedItemToEntry).sort((player1, player2) => {
        const player1LastName = player1.entry.split(' ')[1];
        const player2LastName = player2.entry.split(' ')[1];
        return player1LastName.localeCompare(player2LastName);
    });
};
const mapCoursesFeedToSelectorItems = (feed) => {
    return feed
        .map(mapFeedItemToEntry)
        .sort((course1, course2) => course1.entry.localeCompare(course2.entry));
};

Component.extend({
    tag: 'player-course-collections',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            selectorItems: {
                type: 'array',
                value: [],
            },
            selectedItem: {
                Type: can.Map,
                value: new can.Map({
                    identifier: '',
                    entry: '',
                    hash: '',
                }),
            },
            fetching: {
                type: 'boolean',
                value: true,
            },
            fullCollection: {
                Type: can.Map,
                Value: can.Map,
            },
            fullModalCollection: {
                Type: can.Map,
                Value: can.Map,
            },
            collection: {
                Type: can.Map,
                Value: can.Map,
            },
            isMobile: {
                get() {
                    return (
                        ['small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1
                    );
                },
            },
            dropdownIsHidden: {
                type: 'boolean',
                value: true,
            },
            activeCardIndex: {
                type: 'number',
                value: null,
            },
        },
        setSelectorItems: function(data) {
            const collectionType = this.attr('collectionTags')
                .serialize()[0]
                .toLowerCase();
            let selectorItems = [];
            if (collectionType === 'players' && data.players) {
                selectorItems = mapPlayersFeedToSelectorItems(data.players);
            } else if (collectionType === 'courses' && data.courses) {
                selectorItems = mapCoursesFeedToSelectorItems(data.courses);
            }
            this.attr('selectorItems', selectorItems);
        },
        selectItem: function(itemToSelect) {
            this.attr('selectedItem', {
                identifier: itemToSelect.identifier,
                entry: itemToSelect.entry,
                hash: itemToSelect.hash,
            });
        },
        selectItemByAttribute(attributeName, attributeValue) {
            const selectedItem = this.getSelectorItemByAttribute(
                attributeName,
                attributeValue,
            );
            if (selectedItem) {
                this.selectItem(selectedItem);
                return selectedItem;
            }
            return null;
        },
        getSelectorItemByAttribute(attributeName, attributeValue) {
            return this.attr('selectorItems')
                .serialize()
                .find((item) => item[attributeName] === attributeValue);
        },
    },
    events: {
        inserted() {
            this.registerModalDialog();
            this.registerCollectionFeedService();
            this.fetchSelectorData();
        },

        registerModalDialog() {
            DomEventsManager.subscribe(this);
            this.modalDialog = this.element.find('timeline-modal-dialog');
            const modalDialogViewModel = this.modalDialog.viewModel();

            modalDialogViewModel.on('modalOpened', () => {
                this.viewModel.attr('isModalShown', true);

                if (this.modalSwiper) {
                    this.modalSwiper.params.hashNavigation = {
                        replaceState: true,
                    };
                }
            });

            modalDialogViewModel.on('modalClosed', () => {
                const selectedItemHash = this.viewModel
                    .attr('selectedItem')
                    .serialize().hash;
                urlUtil.setHash(`!${selectedItemHash}`);
                this.viewModel.attr('activeCardIndex', null);
                this.viewModel.attr('isModalShown', false);

                if (this.modalSwiper) {
                    this.modalSwiper.params.hashNavigation = false;
                }
            });
        },

        registerCollectionFeedService() {
            this.feedService = new FeedService({
                feedUrl: playerCourseCollectionFeedURL,
                fromCache: true,
                autoUpdate: false,
            }).on('data', (ev, data) => {
                can.batch.start();
                this.viewModel.attr('fullCollection', data);
                this.viewModel.attr('fullModalCollection', data);
                let cards = this.viewModel.attr('fullCollection.cards');
                if (cards && cards.length) {
                    this.viewModel.attr('collection', {
                        cards: cards.splice(0, 9),
                    });
                }
                can.batch.stop();
                this.viewModel.attr('fetching', false);

                const currentHashItem = this.getCurrentHashItem();
                const currentCardIndex = currentHashItem.card;
                const modalDialogViewModel = this.modalDialog.viewModel();
                if (currentCardIndex && modalDialogViewModel.element.length) {
                    this.showModalSlider(parseInt(currentCardIndex, 10));
                }
            });
        },

        registerSelectionByHash(itemEntry) {
            const selectedItem = this.viewModel.getSelectorItemByAttribute(
                'hash',
                itemEntry,
            );
            if (selectedItem) {
                this.viewModel.selectItem(selectedItem);
                this.fetchCollectionData(selectedItem.identifier);
            }
        },

        fetchSelectorData() {
            can.route.ready();
            can.route(':identifier');

            new FeedService({
                feedUrl: playerCourseAvailableFiltersURL,
                fromCache: true,
                autoUpdate: false,
            })
                .on('data', (ev, data) => {
                    this.viewModel.setSelectorItems(data);

                    const currentHashItem = this.getCurrentHashItem();
                    if (currentHashItem) {
                        this.registerSelectionByHash(
                            currentHashItem.identifier,
                        );
                    }

                    this.registerRouteListener();
                })
                .on('error', (error) => {
                    console.error('Error fetching player-course data', error);
                })
                .fetch();
        },

        registerRouteListener() {
            can.route.bind(
                'change',
                (event, attributeName, type, attributeValue) => {
                    if (!(type === 'set' || type === 'add')) {
                        return;
                    }

                    if (attributeName === 'identifier') {
                        this.registerSelectionByHash(attributeValue);
                    }
                },
            );
        },

        getCurrentHashItem() {
            const hashValue = urlUtil.getHash().slice(2);
            if (hashValue.startsWith('&')) {
                const attributeObject = can.route.attr();
                const currentItemIdentifier = Object.keys(attributeObject)[0];
                return {
                    identifier: currentItemIdentifier,
                    card: attributeObject[currentItemIdentifier],
                };
            } else if (can.route.attr('identifier')) {
                const attributeArray = can.route.attr('identifier').split('=');
                return {
                    identifier: attributeArray[0],
                    card: attributeArray[1],
                };
            }
            return {
                identifier: hashValue,
                card: null,
            };
        },

        fetchCollectionData(identifier) {
            this.feedService.fetch({
                identifier: identifier,
            });
        },

        destroyModalSlider() {
            if (this.modalSwiper) {
                this.modalSwiper.destroy(false, true);
                this.modalSwiper = null;
            }
        },

        initModalSlider(initialSlide) {
            let swiperContainer = can.$(
                '.s-history-experience-collections-modal-slider',
            );
            let slideNumber = swiperContainer.find('.swiper-slide').length;
            let self = this;
            if (slideNumber > 1 && !this.modalSwiper) {
                this.modalSwiper = new Swiper(swiperContainer, {
                    initialSlide: ~~initialSlide,
                    direction: self.viewModel.attr('isMobile')
                        ? 'vertical'
                        : 'horizontal',
                    spaceBetween: 0,
                    mousewheel: true,
                    keyboard: {
                        enabled: true,
                    },
                    centeredSlides: true,
                    slidesPerView: 1,
                    simulateTouch: true,
                    hashNavigation: {
                        replaceState: true,
                    },
                    navigation: {
                        prevEl: swiperContainer.find('.swiper-button--prev'),
                        nextEl: swiperContainer.find('.swiper-button--next'),
                    },
                    on: {
                        init() {
                            if (initialSlide !== 0) {
                                return;
                            }

                            const card = self.viewModel.attr(
                                `fullModalCollection.cards.0`,
                            );
                            const tag = card.collectionTags
                                ? card.collectionTags[0].toLowerCase()
                                : '';

                            dtm.trackHistoryContentView(
                                card.title,
                                card.cardType,
                                card.cardYear,
                                tag,
                            );
                        },
                    },
                });

                this.modalSwiper.on('slideChangeTransitionStart', () =>
                    this.viewModel.attr(
                        'activeCardIndex',
                        this.modalSwiper.activeIndex,
                    ),
                );

                this.modalSwiper.on('slideChangeTransitionEnd', () => {
                    const card = this.viewModel.attr(
                        `fullModalCollection.cards.${this.modalSwiper.activeIndex}`,
                    );
                    const tag = card.collectionTags
                        ? card.collectionTags[0].toLowerCase()
                        : '';

                    dtm.trackHistoryContentView(
                        card.title,
                        card.cardType,
                        card.cardYear,
                        tag,
                    );
                });
            } else if (this.modalSwiper) {
                this.modalSwiper.slideTo(initialSlide, 0);
            }

            this.viewModel.attr('activeCardIndex', initialSlide);
            urlUtil.setHash(
                `!${this.viewModel.attr('selectedItem.hash')}=${initialSlide}`,
            );
        },

        showModalSlider(initialSlide) {
            const modalContainer = this.element.find('.c-history-collections');
            if (this.modalDialog && this.modalDialog.length) {
                this.modalDialog.viewModel().show(modalContainer);
                this.initModalSlider(initialSlide);
            }
        },

        reinitSliders() {
            let activeCardIndex = this.viewModel.attr('activeCardIndex');
            this.destroyModalSlider();
            if (this.viewModel.attr('isModalShown')) {
                this.initModalSlider(activeCardIndex);
            }
        },

        updateSlider() {
            if (this.modalSwiper) {
                this.modalSwiper.update();
            }
        },

        onWindowResize: function() {
            this.debouncedWindowResize();
        },

        debouncedWindowResize: _.debounce(function() {
            this.reinitSliders();
        }, 500),

        onWindowScroll: _.throttle(function() {
            this.throttledWindowScroll();
        }, 100),

        throttledWindowScroll() {
            if (
                (window.innerHeight * 2 + window.pageYOffset ||
                    window.scrollY) >= document.body.offsetHeight
            ) {
                this.addCards();
            }
        },

        addCards() {
            if (this.viewModel.attr('fullCollection.cards.length')) {
                this.viewModel
                    .attr('collection.cards')
                    .push(
                        ...this.viewModel
                            .attr('fullCollection.cards')
                            .splice(0, 3),
                    );
                this.updateSlider();
            }
        },

        doRoute(itemIdentifier) {
            can.route.ready();
            can.route.attr({ identifier: itemIdentifier });
            dtm.trackHistoryTimelineCollectionSelection(itemIdentifier);
        },

        '.s-history-collections-header-dropdown__item-selected click'() {
            this.viewModel.attr(
                'dropdownIsHidden',
                !this.viewModel.attr('dropdownIsHidden'),
            );
        },

        '.s-history-collections-list__item click'(element) {
            const parent = element.closest('.s-history-collections-content');
            parent.find('.s-history-collections-list').addClass('is-hidden');
            parent
                .find('.s-history-collections-cards')
                .removeClass('is-hidden');
        },

        '.s-history-collections-header-dropdown-list__item-text click'(
            element,
        ) {
            this.viewModel.attr('dropdownIsHidden', true);

            this.destroyModalSlider();

            const elementValue = can.trim(element.data('identifier'));
            const selectedItem = this.viewModel.selectItemByAttribute(
                'identifier',
                elementValue,
            );
            dtm.trackSearchByPlayerCourse(selectedItem.entry);
            this.doRoute(selectedItem.hash);
        },

        '.s-history-collections-cards__item click'(element) {
            this.showModalSlider(element.data('index'));
            const videoName = element.data('video-name');
            if (videoName && !this.viewModel.attr('isMobile')) {
                dtm.trackHistoryVideoLauch(
                    videoName,
                    'History Collection Content',
                );
            }
        },

        '{document} click'(element, event) {
            if (
                !$(event.target).closest('.s-history-collections-header__item')
                    .length
            ) {
                this.viewModel.attr('dropdownIsHidden', true);
            }
        },
    },
});
